import "core-js/modules/es.array.push.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.for-each.js";
import "core-js/modules/es.iterator.map.js";
import "core-js/modules/es.set.difference.v2.js";
import "core-js/modules/es.set.intersection.v2.js";
import "core-js/modules/es.set.is-disjoint-from.v2.js";
import "core-js/modules/es.set.is-subset-of.v2.js";
import "core-js/modules/es.set.is-superset-of.v2.js";
import "core-js/modules/es.set.symmetric-difference.v2.js";
import "core-js/modules/es.set.union.v2.js";
/**
 * Chip based search input.
 *
 * Each word the user types becomes a chip (space, comma, tab or enter commits it).
 * Every chip must match a video (title, description, tags, organization, event or
 * composition) so "Carmel" + "2026" finds a video titled "Carmel ..." tagged "2026".
 *
 * v-model (modelValue) is an array of chip strings. `search` is emitted when the user presses
 * enter with nothing pending, or when a chip is removed/cleared.
 */
export default {
  name: 'SearchChips',
  emits: ['update:modelValue', 'search'],
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Search videos...'
    },
    compact: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchOnChange: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      pending: '',
      focused: false
    };
  },
  methods: {
    /**
     * Split free text into chips, trimmed, de-duplicated (case-insensitive),
     * merged with the chips already present.
     */
    splitText(text) {
      return String(text || '').split(/[\s,]+/).map(t => t.trim()).filter(t => t.length);
    },
    addChips(terms) {
      const next = [...this.modelValue];
      const seen = new Set(next.map(t => t.toLowerCase()));
      terms.forEach(term => {
        const key = term.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push(term);
        }
      });
      if (next.length !== this.modelValue.length) {
        this.$emit('update:modelValue', next);
      }
    },
    setFromText(text) {
      this.pending = '';
      this.$emit('update:modelValue', this.splitText(text));
    },
    commitPending() {
      const terms = this.splitText(this.pending);
      this.pending = '';
      if (terms.length) {
        this.addChips(terms);
      }
      return terms.length > 0;
    },
    onKeydown(event) {
      // Let IME composition (e.g. Japanese/Chinese input) finish before we act.
      if (event.isComposing) return;
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          this.commitPending();
          this.$nextTick(() => this.$emit('search', this.modelValue));
          break;
        case ' ':
        case ',':
          event.preventDefault();
          this.commitPending();
          break;
        case 'Tab':
          this.commitPending();
          break;
        case 'Backspace':
          if (this.pending === '' && this.modelValue.length) {
            event.preventDefault();
            this.removeChip(this.modelValue.length - 1);
          }
          break;
        default:
          break;
      }
    },
    onPaste(event) {
      const text = (event.clipboardData || window.clipboardData).getData('text');
      this.addChips(this.splitText(this.pending + ' ' + text));
      this.pending = '';
    },
    onBlur() {
      this.focused = false;
      this.commitPending();
    },
    removeChip(index) {
      const next = [...this.modelValue];
      next.splice(index, 1);
      this.$emit('update:modelValue', next);
      if (this.searchOnChange) {
        this.$nextTick(() => this.$emit('search', next));
      }
    },
    clearAll() {
      this.pending = '';
      this.$emit('update:modelValue', []);
      if (this.searchOnChange) {
        this.$nextTick(() => this.$emit('search', []));
      }
      this.focusInput();
    },
    focusInput() {
      if (this.$refs.input && !this.disabled) {
        this.$refs.input.focus();
      }
    }
  }
};